import React, { useMemo } from "react";
import moment from "moment";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ErrorBar,
    ResponsiveContainer,
    ReferenceLine,
    Tooltip,
} from "recharts";
import { Box, Typography } from "@mui/material";
import OpenWithIcon from '@mui/icons-material/OpenWith';

export default function FetalMovementGraph({records, title, handleOpen, settingType }) {

    // -------- PROCESS DATA ---------
    const data = useMemo(() => {
        if (!records || records.length === 0) return [];

        return records.map((item) => {
            const hasMultiple = item.durations.length > 1;
            // const avg = item.durations.reduce((a, b) => a + b, 0) / item.durations.length;
            const avg = item.averageDuration
            return {
                date: moment(item.date).format("DD MMM"),
                timestamp: moment(item.date).valueOf(),
                avg: avg,   // hide avg for single
                dotValue: avg,                    // dot always shown
                min: Math.min(...item.durations),
                max: Math.max(...item.durations),
                errorLow: hasMultiple ? avg - Math.min(...item.durations) : 0,
                errorHigh: hasMultiple ? Math.max(...item.durations) - avg : 0,
                hasMultiple,
            }
        })

    }, [records]);
    const timestamps = data.map(d => d.timestamp);
    const minTs = Math.min(...timestamps);
    const maxTs = Math.max(...timestamps);
    const yTicks = [0, 5, 10, 15, 20, 25, 30]; // default y-axis ticks

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const { date, dotValue
            } = payload[0].payload;
            return (
                <div style={{
                    backgroundColor: "rgba(251, 251, 251, 1)",
                    padding: "10px",
                    borderRadius: '5px'
                }}>
                    <p>{`Date: ${date}`}</p>
                    <p>{`Value: ${dotValue
                        }`}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div
            style={{
                overflowX: "auto",
                width: "100%",
                height: "100%",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                    display: "none",
                },
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignContent: 'space-between' }}>
                <Typography
                    variant="p"
                    align="left"
                    sx={{ marginLeft: 2, fontSize: "12px",  }}
                >
                    {title}
                </Typography>

                {handleOpen && <OpenWithIcon onClick={() => {
                    handleOpen();
                    settingType(title);
                }} sx={{ alignSelf: 'flex-end', right: 20, cursor: 'pointer', fontSize: 'medium' }} />}
            </Box>
            <div style={{
                width: '98%', height: "100%",
                marginTop: "4px",
            }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data}
                        margin={{ top: 5, right: 10, bottom: 20, left: 10 }}
                    >
                        <CartesianGrid stroke="#e5e5e5" strokeWidth={1} />

                        {/* Y Axis */}
                        <YAxis
                            width={40}
                            ticks={yTicks}
                            domain={[0, 30]}  // constant y-axis range even with no data
                            tick={{ fill: "#000", fontSize: '8px', fontWeight:'bold' }}
                            axisLine={{ stroke: "#000", strokeWidth: 1 }}
                            tickLine={false}
                            label={
                                <text
                                    x={-35}
                                    y={10}
                                    transform="rotate(-90)"
                                    textAnchor="middle"
                                    fontSize={10}
                                    fontWeight="600"
                                    fill="#000"
                                >
                                    <tspan x={-65} dy="4">Fetal Movements</tspan>
                                    <tspan x={-65} dy="12">Avg Time Taken</tspan>
                                </text>
                            }
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <XAxis
                            type="number"
                            dataKey="timestamp"
                            scale="time"
                            tick={{ fill: "#000", fontSize: '8px', fontWeight:'bold' }}
                            domain={[minTs - 24 * 3600 * 1000, maxTs + 24 * 3600 * 1000]} // 1 day padding
                            tickFormatter={(ts) => moment(ts).format("DD MMM")}
                            tickLine={false}
                            axisLine={{ stroke: "#000" }}
                        />
                        <ReferenceLine y={0} stroke="#8a8a8a" strokeWidth={1} />

                        {/* If there's no data, render a dummy line to keep the graph visible */}
                        {data.length > 0 ? (
                            <Line
                                type="monotone"
                                dataKey="avg"
                                dot={false}
                                connectNulls={true}
                                isAnimationActive={false}
                            >
                                <Line
                                    type="monotone"
                                    dataKey="dotValue"
                                    stroke="red"
                                    dot={{ r: 4, fill: "green" }}
                                    connectNulls={true}
                                    isAnimationActive={false}
                                    activeDot={{ r: 6 }}
                                />
                                {data.map((item, index) =>
                                    item.hasMultiple ? (
                                        <ErrorBar
                                            key={index}
                                            data={[item]}
                                            dataKey="dotValue"
                                            width={6}
                                            stroke="red"
                                            direction="y"
                                            low="min"
                                            high="max"
                                        />
                                    ) : null
                                )}
                            </Line>
                    ) : ( 
                            // Placeholder "No data available" message
                            <text
                                x="50%"
                                y="50%"
                                textAnchor="middle"
                                fontSize={10}
                                fontWeight="bold"
                                fill="#888"
                                dy={5}
                            >
                                No data available
                            </text>
                       )}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
